import "react-native-get-random-values";
import * as ExpoImagePicker from "expo-image-picker";
import * as FileSystem from "expo-file-system";
import { Button } from "./Button";
import { ErrorMessage } from "formik";
import { Loading } from "./Loading";
import { None } from "../utils/None";
import { Platform, Text, View } from "react-native";
import { Some } from "../utils/Some";
import { setTestProps } from "../utils/propHelper";
import { v4 as uuidv4 } from "uuid";
import React, { useState } from "react";
import hash from "string-hash-64";

type PickerResult = {
  readonly id: string;
  readonly uri: string;
  readonly base64?: string;
  readonly fileType: string;
};

type Props = {
  readonly afterUpload: (fileId: string) => void;
  readonly data: readonly PickerResult[];
  readonly setFieldError: (field: string, message: string | undefined) => void;
  readonly setFieldValue: (field: string, data: readonly PickerResult[]) => void;
  readonly beforeUpload: () => void;
  readonly onError: (message: string) => void;
  readonly invoiceError?: string;
  readonly testID?: string;
};

const MAX_NUMBER_OF_FILES = 5;

const MAX_FILE_SIZE = 10;

const VALID_FILE_EXTENSIONS = ["jpeg", "png", "webp", "pdf", "jpg"];

export const ImagePicker = ({
  afterUpload,
  beforeUpload,
  data,
  invoiceError,
  onError,
  setFieldValue,
  testID,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const pickDocument = async () => {
    beforeUpload();
    if (data.length >= MAX_NUMBER_OF_FILES) {
      return;
    }

    // In web, When we cancel the image upload, none of the following steps are performed,
    // causing the button to always disable
    if (Platform.OS !== "web") {
      setIsLoading(true);
    }

    const result = await ExpoImagePicker.launchImageLibraryAsync({
      allowsEditing: true,
      base64: true,
      mediaTypes: ExpoImagePicker.MediaTypeOptions.Images,
      quality: 1,
    });

    if (result.canceled) {
      setIsLoading(false);

      return;
    }

    await Promise.all(
      result.assets.map(async asset => {
        // This prevents files that aren't actually images from being selected
        if (asset.width === 0 || asset.height === 0) {
          setIsLoading(false);

          return;
        }

        const localUri = asset.uri;
        const [extension] = localUri.split(".").slice(-1);
        const contentHash = hash(localUri).toString();
        const fileName = `${contentHash}.${extension}`;
        const { base64 } = asset;

        if (None(base64)) {
          setIsLoading(false);
          onError("Something wrong with selected image");

          return;
        }

        const fileInfo = await FileSystem.getInfoAsync(localUri);

        if (!fileInfo.exists) {
          setIsLoading(false);
          // Handle the case where the file does not exist.
          onError(`File does not exist at URI: ${localUri} `);

          return;
        }

        const { size } = fileInfo;

        if (Some(size) && size / (1024 * 1024) >= MAX_FILE_SIZE) {
          setIsLoading(false);
          onError("Please select a file which is below 10mb");

          return;
        }

        // Prevent duplicates
        if (data.some(_ => _.base64 === base64)) {
          setIsLoading(false);
          onError("File already uploaded, please select another");

          return;
        }

        if (!VALID_FILE_EXTENSIONS.includes(extension)) {
          setIsLoading(false);
          onError(`Selected ${extension} file type not supported`);

          return;
        }

        const id = uuidv4();
        const modifiedResult = {
          ...asset,
          base64,
          fileType: `image/${extension}`,
          id,
          mimeType: `image/${extension}`,
          name: fileName,
        };

        setFieldValue("invoice", [...data, modifiedResult]);
        afterUpload(id);
      }),
    );
    setIsLoading(false);
  };

  return (
    <>
      <View style={{ alignItems: "flex-start", width: 200 }}>
        <Button
          customContent={
            isLoading ? (
              <View style={{ minWidth: 160 }}>
                <Loading />
              </View>
            ) : undefined
          }
          disabled={data.length >= MAX_NUMBER_OF_FILES || isLoading}
          onPress={() => {
            pickDocument();
          }}
          testID={testID}
          text="Upload a file"
          type="secondary"
        />
      </View>
      <ErrorMessage name="invoice">
        {error => (
          <View style={{ paddingVertical: 8 }}>
            <Text
              style={{ color: "#D03931", fontSize: 12 }}
              {...setTestProps({ name: `${error}-ErrorMessage-ImagePicker` })}
            >
              {error}
            </Text>
          </View>
        )}
      </ErrorMessage>

      {Some(invoiceError) && (
        <View style={{ paddingVertical: 8 }}>
          <Text
            style={{ color: "#D03931", fontSize: 12 }}
            {...setTestProps({ name: `${invoiceError}-ErrorMessage-ImagePicker` })}
          >
            {invoiceError}
          </Text>
        </View>
      )}
    </>
  );
};
